import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Plus, FolderOpen, Trash2, MapPin, ScanLine, Archive, ArchiveRestore,
  ChevronDown, ChevronRight, Building2, Copy, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getFurnitureProjects,
  createFurnitureProject,
  deleteFurnitureProject,
  setFurnitureProjectArchived,
  duplicateFurnitureProject,
} from '@/lib/api';
import { FurnitureNavBar } from '@/components/furniture/FurnitureNavBar';
import { TypedConfirmDeleteDialog } from '@/components/furniture/ConfirmDeleteDialog';
import { SubProjectWizard } from '@/components/furniture/SubProjectWizard';

function fmtMoney(v) {
  if (v == null || v === '' || isNaN(Number(v))) return null;
  const n = Number(v);
  if (n === 0) return '$0';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function ProjectCard({ p, onOpen, onDelete, onArchiveToggle, onDuplicate, compact = false }) {
  const budget = fmtMoney(p.budget_total);
  const ordered = fmtMoney(p.ordered_total);
  const overBudget = Number(p.ordered_total || 0) > Number(p.budget_total || 0) && Number(p.budget_total || 0) > 0;
  return (
    <Card
      className={`cursor-pointer transition-shadow duration-200 hover:shadow-[0_1px_0_rgba(16,24,40,0.06),0_16px_40px_rgba(16,24,40,0.10)] group ${p.archived ? 'opacity-80' : ''}`}
      onClick={() => onOpen(p.id)}
      data-testid={`project-card-${p.id}`}
    >
      <CardContent className={compact ? 'p-4' : 'p-5'}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`font-heading font-semibold text-[#1f2933] truncate ${compact ? 'text-sm' : ''}`}>{p.name}</h3>
              {p.archived && (
                <span className="text-[10px] uppercase tracking-wider font-medium bg-[#e7e2d4] text-[#5b6773] px-1.5 py-0.5 rounded-full whitespace-nowrap">
                  Archived
                </span>
              )}
            </div>
            {p.address && (
              <p className="text-xs text-[#5b6773] mt-1 flex items-center gap-1 truncate">
                <MapPin className="h-3 w-3 flex-shrink-0" /> {p.address}
              </p>
            )}
          </div>
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            {onDuplicate && (
              <Button
                variant="ghost" size="icon" className="h-8 w-8 text-[#5b6773]"
                onClick={(e) => { e.stopPropagation(); onDuplicate(p); }}
                data-testid={`duplicate-project-${p.id}`}
                title="Duplicate"
              >
                <Copy className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost" size="icon" className="h-8 w-8 text-[#5b6773]"
              onClick={(e) => { e.stopPropagation(); onArchiveToggle(p); }}
              data-testid={`archive-project-${p.id}`}
              title={p.archived ? 'Unarchive' : 'Archive'}
            >
              {p.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost" size="icon" className="h-8 w-8 text-destructive"
              onClick={(e) => { e.stopPropagation(); onDelete(p.id); }}
              data-testid={`delete-project-${p.id}`}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <p className="text-xs text-[#5b6773]/60 mt-3">
          {p.archived && p.archived_at
            ? `Archived ${new Date(p.archived_at).toLocaleDateString()}`
            : new Date(p.created_at).toLocaleDateString()}
        </p>
        {(budget || ordered) && (
          <div className="mt-2 pt-2 border-t border-border/40 flex items-center gap-4 text-[11px]">
            {budget && (
              <div>
                <span className="text-[#5b6773]">Budget</span>{' '}
                <span className="font-semibold text-[#1f2933] tabular-nums">{budget}</span>
              </div>
            )}
            {ordered && (
              <div>
                <span className="text-[#5b6773]">Ordered</span>{' '}
                <span className={`font-semibold tabular-nums ${overBudget ? 'text-red-700' : 'text-emerald-700'}`}>{ordered}</span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MasterProjectGroup({ master, subProjects, onOpen, onDelete, onArchiveToggle, onAddUnit, onDuplicate }) {
  const [open, setOpen] = useState(true);
  const active = subProjects.filter(p => !p.archived);
  // Rollup = master's own items (if any) + sum across all sub-projects
  const budgetTotal = (Number(master.budget_total) || 0) +
    subProjects.reduce((s, p) => s + (Number(p.budget_total) || 0), 0);
  const orderedTotal = (Number(master.ordered_total) || 0) +
    subProjects.reduce((s, p) => s + (Number(p.ordered_total) || 0), 0);
  const overBudget = orderedTotal > budgetTotal && budgetTotal > 0;
  const budgetStr = fmtMoney(budgetTotal);
  const orderedStr = fmtMoney(orderedTotal);
  return (
    <Card
      className="border-[#2d948c]/30 bg-gradient-to-br from-[#2d948c]/[0.04] to-transparent"
      data-testid={`master-group-${master.id}`}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <button
            onClick={() => setOpen(v => !v)}
            className="flex items-start gap-2.5 text-left min-w-0 flex-1"
            data-testid={`master-toggle-${master.id}`}
          >
            <div className="mt-0.5">
              {open
                ? <ChevronDown className="h-4 w-4 text-[#2d948c]" />
                : <ChevronRight className="h-4 w-4 text-[#2d948c]" />}
            </div>
            <Building2 className="h-5 w-5 text-[#2d948c] mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading font-semibold text-[#1f2933] truncate">{master.name}</h3>
                <span className="text-[10px] uppercase tracking-wider font-semibold bg-[#2d948c] text-white px-1.5 py-0.5 rounded-full whitespace-nowrap">
                  Master
                </span>
                <span className="text-xs text-[#5b6773]">
                  {active.length} unit{active.length === 1 ? '' : 's'}
                </span>
              </div>
              {master.address && (
                <p className="text-xs text-[#5b6773] mt-1 flex items-center gap-1 truncate">
                  <MapPin className="h-3 w-3 flex-shrink-0" /> {master.address}
                </p>
              )}
              {(budgetStr || orderedStr) && (
                <div className="mt-2 flex items-center gap-4 text-[11px]">
                  {budgetStr && (
                    <div>
                      <span className="text-[#5b6773]">Total budget</span>{' '}
                      <span className="font-semibold text-[#1f2933] tabular-nums">{budgetStr}</span>
                    </div>
                  )}
                  {orderedStr && (
                    <div>
                      <span className="text-[#5b6773]">Total ordered</span>{' '}
                      <span className={`font-semibold tabular-nums ${overBudget ? 'text-red-700' : 'text-emerald-700'}`}>{orderedStr}</span>
                    </div>
                  )}
                  {budgetTotal > 0 && orderedTotal > 0 && (
                    <div className="text-[#5b6773]">
                      <span className="tabular-nums">{Math.round((orderedTotal / budgetTotal) * 100)}%</span> of budget
                    </div>
                  )}
                </div>
              )}
            </div>
          </button>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); onAddUnit(master); }}
              className="gap-1.5 h-8"
              data-testid={`master-add-unit-${master.id}`}
            >
              <Plus className="h-3.5 w-3.5" /> Add unit
            </Button>
            <Button
              variant="ghost" size="icon" className="h-8 w-8 text-[#5b6773]"
              onClick={(e) => { e.stopPropagation(); onOpen(master.id); }}
              title="Open master"
              data-testid={`master-open-${master.id}`}
            >
              <FolderOpen className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost" size="icon" className="h-8 w-8 text-[#5b6773]"
              onClick={(e) => { e.stopPropagation(); onArchiveToggle(master); }}
              title={master.archived ? 'Unarchive' : 'Archive'}
              data-testid={`master-archive-${master.id}`}
            >
              {master.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost" size="icon" className="h-8 w-8 text-destructive"
              onClick={(e) => { e.stopPropagation(); onDelete(master.id); }}
              title="Delete master"
              data-testid={`master-delete-${master.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {open && (
          <div className="mt-4 pl-3 sm:pl-7 border-l-2 border-[#2d948c]/15">
            {subProjects.length === 0 ? (
              <p className="text-sm text-[#5b6773] italic py-3">
                No units yet — click <strong>Add unit</strong> to create the first one.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {subProjects.map(p => (
                  <ProjectCard
                    key={p.id} p={p}
                    onOpen={onOpen}
                    onDelete={onDelete}
                    onArchiveToggle={onArchiveToggle}
                    onDuplicate={onDuplicate}
                    compact
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function FurnitureProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(null); // null | 'project' | 'master'
  const [wizardState, setWizardState] = useState(null); // null | { master, sourceProject, mode }
  const [newName, setNewName] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const data = await getFurnitureProjects({ includeArchived: true });
      setProjects(data);
    } catch { toast.error('Failed to load projects'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openNew = (mode) => {
    setShowNew(mode);
    setNewName('');
    setNewAddress('');
  };

  const closeNew = () => {
    setShowNew(null);
    setNewName('');
    setNewAddress('');
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const payload = { name: newName.trim(), address: newAddress.trim() };
      if (showNew === 'master') payload.is_master = true;
      const p = await createFurnitureProject(payload);
      closeNew();
      await load();
      if (showNew === 'master') {
        toast.success('Master project created');
      } else {
        toast.success('Project created');
        navigate(`/furniture-projects/${p.id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create project');
    }
  };

  const handleAddUnit = (master) => {
    setWizardState({ master, sourceProject: null, mode: 'add' });
  };

  const handleDuplicate = (project) => {
    // If the project belongs to a master, use the wizard with master context.
    // Otherwise, fall back to a one-click duplicate (no wizard needed for
    // standalone projects since there's no diff context).
    if (project.parent_id) {
      const master = projects.find(p => p.id === project.parent_id && p.is_master);
      if (master) {
        setWizardState({ master, sourceProject: project, mode: 'duplicate' });
        return;
      }
    }
    // Standalone duplicate
    duplicateFurnitureProject(project.id, {})
      .then(copy => { toast.success(`Duplicated as "${copy.name}"`); load(); })
      .catch(e => toast.error(e?.response?.data?.detail || 'Failed to duplicate'));
  };

  const handleDelete = (id) => {
    const project = projects.find(p => p.id === id);
    if (!project) return;
    setPendingDelete(project);
  };

  const performDelete = async (id) => {
    try {
      await deleteFurnitureProject(id);
      setProjects(prev => prev.filter(p => p.id !== id));
      toast.success('Project deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleArchiveToggle = async (p) => {
    const nextArchived = !p.archived;
    setProjects(prev => prev.map(x => x.id === p.id
      ? { ...x, archived: nextArchived, archived_at: nextArchived ? new Date().toISOString() : null }
      : x));
    try {
      await setFurnitureProjectArchived(p.id, nextArchived);
      toast.success(nextArchived ? 'Project archived' : 'Project unarchived');
      if (nextArchived) setArchivedOpen(true);
    } catch {
      setProjects(prev => prev.map(x => x.id === p.id ? p : x));
      toast.error('Failed to update');
    }
  };

  const { masters, standalone, archived } = useMemo(() => {
    const masters = [];
    const standalone = [];
    const archivedList = [];
    const byParent = new Map();
    for (const p of projects) {
      if (p.archived && !p.is_master) {
        archivedList.push(p);
        continue;
      }
      if (p.is_master) {
        masters.push(p);
      } else if (p.parent_id) {
        const arr = byParent.get(p.parent_id) || [];
        arr.push(p);
        byParent.set(p.parent_id, arr);
      } else {
        standalone.push(p);
      }
    }
    // Attach sub-projects to their master
    const mastersWithSubs = masters.map(m => ({
      ...m,
      _subs: (byParent.get(m.id) || []),
    }));
    // Orphaned subs (parent missing) — show in standalone
    const orphans = [];
    for (const [pid, subs] of byParent) {
      if (!masters.find(m => m.id === pid)) orphans.push(...subs);
    }
    return {
      masters: mastersWithSubs,
      standalone: [...standalone, ...orphans],
      archived: archivedList,
    };
  }, [projects]);

  const dialogTitle = showNew === 'master'
    ? 'New Master Project'
    : 'New Furniture Project';

  return (
    <div className="min-h-screen bg-[#fbfaf7]">
      <FurnitureNavBar />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-8 sm:mb-10 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-[#1f2933]" data-testid="furniture-projects-title">
              Furniture Projects
            </h1>
            <p className="text-sm text-[#5b6773] mt-1">Track furnishing budgets, items, and deliveries</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => navigate('/furniture-projects/receipt-processing')}
              variant="outline"
              className="gap-2"
              data-testid="process-receipt-btn"
            >
              <ScanLine className="h-4 w-4" /> Process Receipt
            </Button>
            <Button
              onClick={() => openNew('master')}
              variant="outline"
              className="gap-2 border-[#2d948c]/40 text-[#2d948c] hover:bg-[#2d948c]/5"
              data-testid="new-master-btn"
            >
              <Layers className="h-4 w-4" /> New Master
            </Button>
            <Button onClick={() => openNew('project')} className="gap-2" data-testid="new-project-btn">
              <Plus className="h-4 w-4" /> New Project
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => (
              <Card key={i} className="animate-pulse h-[120px]"><CardContent /></Card>
            ))}
          </div>
        ) : masters.length === 0 && standalone.length === 0 && archived.length === 0 ? (
          <Card className="border-dashed border-2 bg-transparent">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <FolderOpen className="h-12 w-12 text-[#5b6773] mb-4 opacity-50" />
              <p className="text-[#5b6773] font-medium mb-1">No projects yet</p>
              <p className="text-xs text-[#5b6773]/70 mb-4">Create your first furniture project to start tracking</p>
              <Button onClick={() => openNew('project')} variant="outline" className="gap-2" data-testid="empty-new-project-btn">
                <Plus className="h-4 w-4" /> New Project
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {masters.length > 0 && (
              <div className="space-y-4">
                {masters.map(m => (
                  <MasterProjectGroup
                    key={m.id}
                    master={m}
                    subProjects={m._subs}
                    onOpen={(id) => navigate(`/furniture-projects/${id}`)}
                    onDelete={handleDelete}
                    onArchiveToggle={handleArchiveToggle}
                    onAddUnit={handleAddUnit}
                    onDuplicate={handleDuplicate}
                  />
                ))}
              </div>
            )}

            {standalone.length > 0 && (
              <div>
                {masters.length > 0 && (
                  <p className="text-[10px] uppercase tracking-wider text-[#5b6773] font-semibold mb-3">
                    Other projects
                  </p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {standalone.map(p => (
                    <ProjectCard
                      key={p.id} p={p}
                      onOpen={(id) => navigate(`/furniture-projects/${id}`)}
                      onDelete={handleDelete}
                      onArchiveToggle={handleArchiveToggle}
                      onDuplicate={handleDuplicate}
                    />
                  ))}
                </div>
              </div>
            )}

            {archived.length > 0 && (
              <div className="mt-10" data-testid="archived-projects-section">
                <button
                  onClick={() => setArchivedOpen(v => !v)}
                  className="flex items-center gap-2 text-sm font-medium text-[#5b6773] hover:text-[#1f2933] transition-colors mb-4"
                  data-testid="archived-projects-toggle"
                >
                  {archivedOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <Archive className="h-4 w-4" />
                  Archived projects
                  <span className="text-xs text-[#9ca3af]">({archived.length})</span>
                </button>
                {archivedOpen && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {archived.map(p => (
                      <ProjectCard
                        key={p.id} p={p}
                        onOpen={(id) => navigate(`/furniture-projects/${id}`)}
                        onDelete={handleDelete}
                        onArchiveToggle={handleArchiveToggle}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={!!showNew} onOpenChange={(o) => { if (!o) closeNew(); }}>
        <DialogContent data-testid="new-project-dialog">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {showNew === 'master' && (
              <p className="text-xs text-[#5b6773] bg-[#2d948c]/10 border border-[#2d948c]/20 rounded-md px-3 py-2">
                A master project groups multiple units in the same building.
                You&apos;ll be able to add individual units to it after creating.
              </p>
            )}
            <div>
              <label className="text-sm font-medium text-[#1f2933]">
                {showNew === 'master' ? 'Master / Building Name' : 'Project Name'}
              </label>
              <Input
                value={newName} onChange={e => setNewName(e.target.value)}
                placeholder={
                  showNew === 'master' ? 'e.g. 123 Main St (Master)' :
                  'e.g. 123 Main St Unit 4'
                }
                autoFocus
                data-testid="new-project-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1f2933]">
                {showNew === 'master' ? 'Building Address' : 'Address'}
              </label>
              <Input
                value={newAddress} onChange={e => setNewAddress(e.target.value)}
                placeholder="Full address"
                data-testid="new-project-address-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeNew}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!newName.trim()} data-testid="create-project-submit">
              {showNew === 'master' ? 'Create Master' : 'Create Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SubProjectWizard
        open={!!wizardState}
        onOpenChange={(o) => { if (!o) setWizardState(null); }}
        master={wizardState?.master}
        mode={wizardState?.mode}
        sourceProject={wizardState?.sourceProject}
        onCreated={() => { setWizardState(null); load(); }}
      />

      <TypedConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
        title={pendingDelete ? `Delete "${pendingDelete.name}" forever?` : 'Delete project?'}
        description={pendingDelete?.is_master
          ? 'Deleting a master is only allowed when it has no sub-projects. Move or delete its units first.'
          : 'All rooms, items, receipts, and designer hours will be permanently removed. Consider Archiving instead — it hides the project but keeps everything intact.'}
        phrase="DELETE"
        destructiveLabel="Delete forever"
        onConfirm={() => { if (pendingDelete) performDelete(pendingDelete.id); setPendingDelete(null); }}
      />
    </div>
  );
}
